import { t } from "../../../../utils/i18n";
import React, { useEffect, useState } from "react";
import { formatCardNumber, maskCardNumber } from "../Card/helpers";
import { cardImages } from "../../Requests/New/helpers";
import { getRequest } from "../../../../utils/httpRequest";
import { decryptSecret } from "./helpers";

const VirtualCard = ({ card, cardSecretKey }) => {
  const viewCardDetails = async (e) => {
    e.preventDefault();

    if (decryptedCardNumber && decryptedCVC) {
      setViewAllNumbers(true);
      return;
    }

    setLoading(true);
    getRequest(`/api/v1/cards/details/${card.id}/get_encrypted_details`)
      .then((response) => {
        decryptCardDetails(response);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching encrypted card details:", error);
      });
  };

  const decryptCardDetails = async (encryptedDetails) => {
    const decryptedCardNumber = await decryptSecret(
      encryptedDetails.encryptedPan.data,
      encryptedDetails.encryptedPan.iv,
      cardSecretKey
    );
    const decryptedCVC = await decryptSecret(
      encryptedDetails.encryptedCvc.data,
      encryptedDetails.encryptedCvc.iv,
      cardSecretKey
    );
    setDecryptedCardNumber(decryptedCardNumber);
    setDecryptedCVC(decryptedCVC);
    setViewAllNumbers(true);
    setLoading(false);
  };

  const [viewAllNumbers, setViewAllNumbers] = useState(false);
  const [decryptedCardNumber, setDecryptedCardNumber] = useState("");
  const [decryptedCVC, setDecryptedCVC] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleViewAllNumbers = (e) => {
    e.preventDefault();
    setViewAllNumbers(!viewAllNumbers);
  };

  const cardImageUrl =
    cardImages[card.card_type] ||
    "https://via.placeholder.com/400x250?text=Card+Image";

  const cardExpiry = () => {
    if (viewAllNumbers) {
      return `${card.exp_month}/${card.exp_year}`;
    } else {
      return "XX/XXXX";
    }
  };

  return (
    <div className="col-md-6 col-lg-4 mb-4">
      <div className="modern-card-container modern-fade-in h-100 d-flex flex-column">
        <div className="modern-card-header" style={{ background: "#4c3f84" }}>
          <i
            className={`fa fa-${
              card.card_type === "virtual" ? "wifi" : "credit-card"
            }`}
          ></i>
          <h5>
            {card.card_type
              ? card.card_type.replace(/_/g, " ").toUpperCase()
              : t("cards.details.card_fallback")}
          </h5>
        </div>
        <img
          src={cardImageUrl}
          alt={t("cards.details.card_alt", { type: card.card_type })}
          style={{
            width: "100%",
            height: "180px",
            objectFit: "contain",
            backgroundColor: "#ffffff",
            borderTopLeftRadius: "0",
            borderTopRightRadius: "0",
          }}
        />
        <div className="modern-card-body flex-grow-1 d-flex flex-column">
          {viewAllNumbers ? (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setViewAllNumbers(false);
              }}
              className="modern-link"
              style={{ fontSize: "0.8rem" }}
            >
              <i className="fa fa-eye-slash"></i>
              {t("cards.details.hide_details")}
            </a>
          ) : (
            <div>
              <a
                href="#"
                onClick={viewCardDetails}
                className="modern-link"
                style={{ fontSize: "0.8rem" }}
              >
                <i className="fa fa-eye"></i>
                {t("cards.details.show_details")}
              </a>
              {loading && (
                <div className="py-1">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">{t("cards.common.loading")}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="mb-3">
            <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              {t("cards.details.card_number_caps")}
            </p>
            <h5 className="font-weight-bold">
              {viewAllNumbers
                ? formatCardNumber(decryptedCardNumber)
                : `XXXX-XXXX-XX-${card.card_number}`}
            </h5>

            <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              {t("cards.details.card_expiry_caps")}
            </p>
            <h5 className="font-weight-bold">{cardExpiry()}</h5>
            <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              {t("cards.details.card_cvv_caps")}
            </p>
            <h5 className="font-weight-bold">
              {viewAllNumbers ? decryptedCVC : "XXX"}
            </h5>
          </div>
          <div className="mb-3">
            <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              {t("cards.details.cardholder_caps")}
            </p>
            <p className="font-weight-bold">
              {`${card.first_name || t("cards.common.not_available")} ${card.last_name || ""}`.trim()}
            </p>
          </div>

          <div className="mb-4">
            <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
              {t("cards.details.status_caps")}
            </p>
            <span
              style={{
                color: "var(--text-dark, #2d3436)",
                fontSize: "0.9rem",
                fontWeight: "500",
              }}
            >
              <i
                className={`fa fa-${
                  card.status === "active" ? "check-circle" : "pause-circle"
                }`}
                style={{
                  marginRight: "0.5rem",
                  color:
                    card.status === "active"
                      ? "var(--success-color, #00b894)"
                      : "var(--warning-color, #fdcb6e)",
                }}
              ></i>
              {card.status ? card.status.toUpperCase() : t("cards.common.not_available")}
            </span>
          </div>

          <div
            className="mt-auto d-flex flex-column flex-sm-row"
            style={{ gap: "0.5rem" }}
          >
            <a
              className="modern-btn modern-btn-primary flex-grow-1"
              href={`/cards/details/${card.id}`}
            >
              <i className="fa fa-search"></i> {t("cards.details.view_details")}
            </a>
            {card.status === "active" && (
              <a
                className="modern-btn modern-btn-outline flex-grow-1"
                href={`/cards/${card.id}/card_loads/new`}
              >
                <i className="fa fa-plus-circle"></i> {t("cards.details.load_card")}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualCard;
